import { EchecApi } from '../../partage/api/client';
import type { Bulle, MaillonDeBoucle, RetenuePar } from '../../partage/api/types';

/**
 * Ce qu'une bulle attend, lu d'une seule façon (chantier nettoyage-regles, ticket 02), sans
 * rendu. Le serveur le calcule (`Bulle.retenuePar`, dans son ordre) ; chaque écran n'en montre
 * qu'une part, et c'est cette part qui se règle ici, par une lecture nommée.
 *
 * Les cinq lectures sont celles d'avant le ticket, telles quelles : les unifier changerait ce
 * qu'Igor voit (la situation d'une feuille compte ce qu'elle contient, la fiche du téléphone
 * non), c'est à lui d'en décider.
 */
export const LECTURES_DE_L_ATTENTE = {
  /** La pastille « Attend » de la fiche du téléphone : ses liens et ceux d'une bulle qui la contient, chacun une fois ; rien une fois acquise. */
  ficheDuTelephone: { par: ['transversale', 'heritee'], uneFoisChacune: true, rienUneFoisAcquise: true },
  /** Le panneau Réglages du téléphone : les attentes héritées, chacune une fois ; rien une fois acquise. */
  reglagesDuTelephone: { par: ['heritee'], uneFoisChacune: true, rienUneFoisAcquise: true },
  /** Le bloc Conditions de l'ordinateur : les attentes héritées, toutes. */
  conditions: { par: ['heritee'], uneFoisChacune: false, rienUneFoisAcquise: false },
  /** Une bulle de la Hiérarchie : ses liens posés à la main, tous. */
  hierarchie: { par: ['transversale'], uneFoisChacune: false, rienUneFoisAcquise: false },
  /** La situation d'une feuille : tout ce qui la retient, ce qu'elle contient compris. */
  situationDeFeuille: { par: ['enfant', 'transversale', 'heritee'], uneFoisChacune: false, rienUneFoisAcquise: false },
} as const;

export type LectureDeLAttente = keyof typeof LECTURES_DE_L_ATTENTE;
type SorteLue<L extends LectureDeLAttente> = (typeof LECTURES_DE_L_ATTENTE)[L]['par'][number];

/** Ce qu'elle attend, selon la lecture de l'écran, dans l'ordre du serveur. */
export function ceQuElleAttendSelon<L extends LectureDeLAttente>(
  bulle: Pick<Bulle, 'etat' | 'retenuePar'>,
  lecture: L
): (RetenuePar & { par: SorteLue<L> })[] {
  const { par, uneFoisChacune, rienUneFoisAcquise } = LECTURES_DE_L_ATTENTE[lecture];
  if (rienUneFoisAcquise && bulle.etat === 'fait') return [];
  const sortes: readonly string[] = par;
  const vues = new Set<string>();
  const lues: RetenuePar[] = [];
  for (const r of bulle.retenuePar) {
    if (!sortes.includes(r.par)) continue;
    if (uneFoisChacune) {
      if (vues.has(r.id)) continue;
      vues.add(r.id);
    }
    lues.push(r);
  }
  return lues as (RetenuePar & { par: SorteLue<L> })[];
}

/**
 * Les comptes du bloc Conditions : `posees`, ce qui fait paraître le bloc (la bulle qui la
 * contient n'y compte pas : toute bulle est dans une autre) ; `combienAttend`, ce que dit la
 * ligne repliée (« Attend 2 choses »), la bulle qui la contient comprise.
 */
export function comptesDesConditions(liste: {
  dansUneBulle: boolean;
  attend: number;
  heritees: number;
  retient: number;
}): { posees: number; combienAttend: number } {
  return {
    posees: liste.attend + liste.heritees + liste.retient,
    combienAttend: (liste.dansUneBulle ? 1 : 0) + liste.attend + liste.heritees,
  };
}

/** Le refus d'un lien parce qu'il fermerait une boucle. */
export function estUnCycle(erreur: unknown): erreur is EchecApi {
  return erreur instanceof EchecApi && erreur.code === 'cycle';
}

/** Une condition déjà posée de la boucle : `attend` attend `attendu`, par le lien `lienId`. */
export interface ConditionDeLaBoucle {
  lienId: string;
  attend: MaillonDeBoucle;
  attendu: MaillonDeBoucle;
}

/** Les conditions posées à la main le long du tour, avec le maillon qu'elles font attendre. */
export function conditionsPosees(tour: MaillonDeBoucle[]): ConditionDeLaBoucle[] {
  return tour.flatMap((attend, i) => {
    const attendu = tour[(i + 1) % tour.length];
    return attend.lienId && attendu ? [{ lienId: attend.lienId, attend, attendu }] : [];
  });
}
